if (!customElements.get('wr-full-hero-carousel')) {
  class WrFullHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initializing) return;

      this.initializing = true;
      window.requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.initialized) return;

      this.initializing = false;
      this.initialized = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.carouselEnabled = this.dataset.carouselEnabled === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplayMobile = this.dataset.autoplayMobile === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.delay = Number(this.dataset.delay || 4000);
      this.isMobile = window.matchMedia('(max-width: 768px)').matches;
      this.viewport = this.querySelector('[data-wrfhc-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-wrfhc-slide]'));
      this.prevButton = this.querySelector('[data-wrfhc-prev]');
      this.nextButton = this.querySelector('[data-wrfhc-next]');
      this.dots = Array.from(this.querySelectorAll('[data-wrfhc-dot]'));
      this.currentIndex = Math.max(this.slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      this.intervalId = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchLocked = false;

      if (!this.viewport || this.slides.length === 0) {
        this.initialized = false;
        return;
      }

      this.handleResize = () => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia('(max-width: 768px)').matches;
        this.measureViewport();

        if (wasMobile === this.isMobile) return;

        this.stop();
        this.start();
      };

      this.handleImageLoad = () => this.measureViewport();
      this.handleMouseEnter = () => this.stop();
      this.handleMouseLeave = () => this.start();
      this.handlePrevClick = () => {
        this.showPrevious(true);
      };
      this.handleNextClick = () => {
        this.showNext(true);
      };
      this.handleDotClick = (event) => {
        const index = Number(event.currentTarget.dataset.index);

        if (Number.isNaN(index)) return;
        this.showSlide(index, true);
      };
      this.handleTouchStart = (event) => {
        if (!this.carouselEnabled || this.slides.length < 2 || !this.isMobile) return;

        this.touchLocked = Boolean(event.target.closest('a, button'));

        if (this.touchLocked) return;

        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.stop();
      };
      this.handleTouchEnd = (event) => {
        if (this.touchStartX === null || this.touchLocked) {
          this.touchLocked = false;
          this.touchStartX = null;
          this.touchStartY = null;
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
          if (deltaX > 0) {
            this.showPrevious(true);
          } else {
            this.showNext(true);
          }
        } else {
          this.start();
        }
      };

      this.imageElements = Array.from(this.querySelectorAll('img'));
      this.imageElements.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.handleImageLoad);
      });

      window.addEventListener('resize', this.handleResize);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.handleDotClick);
      });

      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd, { passive: true });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }

      this.showSlide(this.currentIndex, false);
      this.measureViewport();

      if (!this.carouselEnabled || this.slides.length < 2) return;
      this.start();
    }

    disconnectedCallback() {
      this.initializing = false;
      this.stop();
      window.removeEventListener('resize', this.handleResize);

      this.imageElements?.forEach((image) => {
        image.removeEventListener('load', this.handleImageLoad);
      });

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      this.dots?.forEach((dot) => {
        dot.removeEventListener('click', this.handleDotClick);
      });

      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchend', this.handleTouchEnd);

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
      }
    }

    measureViewport() {
      if (!this.viewport || this.slides.length === 0) return;

      const maxHeight = this.slides.reduce((height, slide) => Math.max(height, slide.offsetHeight), 0);

      if (maxHeight > 0) {
        this.viewport.style.height = `${maxHeight}px`;
      }
    }

    start() {
      if (!this.canAutoplay() || this.intervalId) return;

      this.intervalId = window.setInterval(() => {
        this.showNext(false);
      }, this.delay);
    }

    stop() {
      if (!this.intervalId) return;

      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    canAutoplay() {
      if (!this.carouselEnabled || this.reducedMotion || this.slides.length < 2 || !this.autoplay) {
        return false;
      }

      return !this.isMobile || this.autoplayMobile;
    }

    showPrevious(resetAutoplay = false) {
      const nextIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.showSlide(nextIndex, resetAutoplay);
    }

    showNext(resetAutoplay = false) {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.showSlide(nextIndex, resetAutoplay);
    }

    showSlide(index, resetAutoplay = false) {
      if (this.slides.length === 0) return;

      const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        const slideButton = slide.querySelector('.wrfhc__button');

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (slideButton) {
          slideButton.tabIndex = isActive ? 0 : -1;
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.currentIndex = nextIndex;
      this.measureViewport();

      if (resetAutoplay) {
        this.stop();
        this.start();
      }
    }
  }

  customElements.define('wr-full-hero-carousel', WrFullHeroCarousel);
}
